import { Badge, Title } from "@mantine/core";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Content } from "shared/components/global/Content";
import { TopicCreateForm, TopicEditForm, TopicFormType } from "shared/components/organization/topics/TopicForm";
import { features } from "shared/graphql/features";
import { topicsQueryOptions, useUpdateTopic, useUpdateTopicParent } from "shared/graphql/topics";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { queryClient } from "src/queryClient";
import { z } from "zod";

const TopicCreateContainer = () => {
    const { required, category } = Route.useSearch();
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.rootTopic);

    return (
        <Content style={{ alignItems: "Center", height: "100%" }} paper>
            <Content.Heading backable>
                <Title order={3}>Create Topic</Title>
                {required && <Badge color="red">Required</Badge>}
            </Content.Heading>
            <TopicCreateForm parentId={category ?? rootTopic.id} />
        </Content>
    );
};

const TopicEditContainer = () => {
    const { topicId } = Route.useParams();
    const {
        data: { topic },
    } = useSuspenseQuery(topicsQueryOptions.topic(topicId));

    const { updateTopicAsync } = useUpdateTopic();
    const { updateTopicParentAsync } = useUpdateTopicParent();

    function handleEdit(data: TopicFormType) {
        const promises = [];
        const parentDiff: string[] = [];
        if (data.name != topic.name || data.description != topic.description) {
            const { name, description } = data;
            promises.push(updateTopicAsync({ topicId: topic.id, params: { name, description } }));
        }
        if (data.parent != topic.parent?.id) {
            if (!data.parent) return;
            parentDiff.push(data.parent);
            promises.push(updateTopicParentAsync({ topicId: topic.id, parentId: data.parent }));
        }
        Promise.all(promises)
            .then(async () =>
                Promise.all([...parentDiff.map((id) => qk.invalidate("topic", "id", id)), qk.invalidate("topic", "id", topicId)]).catch(
                    logger.error,
                ),
            )
            .then(() => notify.show.success({ message: "Updated Topic" }))
            .catch(logger.error);
    }

    return (
        <Content style={{ alignItems: "Center", height: "100%" }} paper>
            <Content.Heading backable>
                <Title order={3}>{topic.isCategory ? "Edit Category" : "Edit Topic"}</Title>
                {topic.isRequired && <Badge color="red">Required</Badge>}
            </Content.Heading>
            <TopicEditForm topic={topic} onSubmit={handleEdit} parentId={topic.parent!.id} />
        </Content>
    );
};

const TopicContainer = () => {
    const { topicId } = Route.useParams();

    features.useGuard("HAS_TOPICS");

    return topicId == "create" ? <TopicCreateContainer /> : <TopicEditContainer />;
};

/**
 * @public
 */
export const Route = createFileRoute("/_auth/organization/topics/$topicId")({
    validateSearch: z.object({ required: z.boolean().optional(), category: z.string().optional() }),
    loader: async ({ params: { topicId } }) => {
        if (topicId != "create") await queryClient.ensureQueryData(topicsQueryOptions.topic(topicId));
    },
    component: TopicContainer,
});
